import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Hash, Sparkles, Book, Code, MessageSquare } from 'lucide-react';

interface SampleContentCreatorProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}

const sampleFiles = [
  {
    type: 'document',
    name: 'Getting Started Guide',
    path: 'documents/getting-started.md',
    description: 'Introduction to Phrasewright and @ references',
    icon: Book,
    tags: ['guide', 'intro'],
  },
  {
    type: 'document',
    name: 'API Documentation Template',
    path: 'documents/api-template.md',
    description: 'Template for API documentation with reusable components',
    icon: FileText,
    tags: ['template', 'api'],
  },
  {
    type: 'phrase',
    name: 'Authentication Intro',
    path: 'phrases/auth-intro.md',
    description: 'Standard authentication explanation',
    icon: Hash,
    tags: ['auth', 'security'],
  },
  {
    type: 'phrase',
    name: 'Error Handling',
    path: 'phrases/error-handling.md',
    description: 'Common error handling patterns',
    icon: Code,
    tags: ['error', 'patterns'],
  },
  {
    type: 'phrase',
    name: 'Support Contact',
    path: 'phrases/support-contact.md',
    description: 'Standard support contact information',
    icon: MessageSquare,
    tags: ['contact', 'support'],
  },
];

export function SampleContentCreator({ enabled, onToggle }: SampleContentCreatorProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="font-medium">Include Sample Content</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Create example files to help you understand the @ reference system
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={onToggle} />
      </div>

      {enabled && (
        <Card className="bg-muted/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Sample Files to Create</CardTitle>
            <CardDescription className="text-xs">
              These files will demonstrate how @ references work
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {sampleFiles.map((file, index) => {
                  const Icon = file.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-2 rounded-md border bg-background"
                    >
                      <div
                        className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                          file.type === 'document'
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-green-50 text-green-600'
                        }`}
                      >
                        <Icon className="h-3 w-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium truncate">{file.name}</span>
                          <Badge variant="outline" className="text-xs h-4 px-1">
                            {file.type}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{file.description}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {file.tags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs h-4 px-1">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Sample content templates that will be created
export const SAMPLE_CONTENT = {
  'documents/getting-started.md': `---
title: Getting Started with Phrasewright
type: document
tags: [guide, intro, getting-started]
---

# Getting Started with Phrasewright

Welcome to Phrasewright! This guide will help you understand the power of @ references for creating reusable, intelligent content.

## What are @ References?

@ References allow you to link and include content from other files in your project. This creates a powerful system for building documents from reusable components.

## Quick Examples

### Authentication Documentation
When documenting APIs, you can reuse common explanations:

@phrases/auth-intro.md

### Error Handling
Standard error patterns can be shared across documents:

@phrases/error-handling.md

### Contact Information
Keep support information consistent:

@phrases/support-contact.md

## Why This Matters

With @ references, you can:

1. **Maintain Consistency**: Update content in one place, see it everywhere
2. **Build Complex Documents**: Assemble comprehensive documentation from smaller pieces
3. **Perfect for AI**: Create prompts and documents that reference your knowledge base
4. **Save Time**: Write once, reference everywhere

## Next Steps

1. Create your first phrase file
2. Reference it in a document
3. See the live preview in action
4. Build your own content system

Happy writing!
`,

  'documents/api-template.md': `---
title: API Documentation Template
type: document
tags: [template, api, documentation]
---

# {{API_NAME}} API Documentation

## Overview
Brief description of your API and its purpose.

## Authentication
@phrases/auth-intro.md

## Base URL
\`\`\`
https://api.example.com/v1
\`\`\`

## Endpoints

### GET /users
Returns a list of users.

#### Response
\`\`\`json
{
  "users": [
    {
      "id": 1,
      "name": "John Doe",
      "email": "john@example.com"
    }
  ]
}
\`\`\`

### POST /users
Creates a new user.

## Error Handling
@phrases/error-handling.md

## Rate Limiting
This API is rate limited to 1000 requests per hour per API key.

## Support
@phrases/support-contact.md
`,

  'phrases/auth-intro.md': `---
title: Authentication Introduction
type: phrase
tags: [auth, security, api]
category: authentication
keywords: [auth, token, api-key, bearer]
---

## Authentication

This API uses Bearer token authentication. Include your API key in the Authorization header:

\`\`\`
Authorization: Bearer YOUR_API_KEY
\`\`\`

You can obtain your API key from your dashboard in the API Keys section.

**Important**: Keep your API key secure and never expose it in client-side code.
`,

  'phrases/error-handling.md': `---
title: Error Handling
type: phrase
tags: [error, patterns, api]
category: errors
keywords: [error, http, status, code]
---

## Error Responses

All errors return a consistent JSON structure:

\`\`\`json
{
  "error": {
    "code": "INVALID_REQUEST",
    "message": "The request is missing required parameters",
    "details": {
      "missing_fields": ["email"]
    }
  }
}
\`\`\`

### Common Error Codes

- **400 Bad Request**: Invalid request parameters
- **401 Unauthorized**: Invalid or missing authentication
- **403 Forbidden**: Insufficient permissions
- **404 Not Found**: Resource not found
- **429 Too Many Requests**: Rate limit exceeded
- **500 Internal Server Error**: Server error
`,

  'phrases/support-contact.md': `---
title: Support Contact Information
type: phrase
tags: [contact, support, help]
category: support
keywords: [support, contact, help, email]
---

## Need Help?

If you have questions or need assistance:

- **Documentation**: Check our comprehensive docs first
- **Email Support**: support@example.com
- **Response Time**: We typically respond within 24 hours
- **Status Page**: Check service status at status.example.com

For urgent issues, please include "URGENT" in your email subject line.
`,
} as const;
